import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';
import {applicativeValidation, lift} from 'Shared/utilities/ToValidation';
import {sequenceT} from 'fp-ts/lib/Apply';
import {Either, getOrElse, left, map as mapEither, mapLeft, right} from 'fp-ts/lib/Either';
import {pipe} from 'fp-ts/lib/pipeable';

const userNameNotEmpty = (name: string): Either<string, string> =>
  name && name.length > 0 ? right(name) : left('User name must not be empty');

const userNameNotEmptyV = lift(userNameNotEmpty);

const validateUserName = (userName: string) =>
  pipe(
    sequenceT(applicativeValidation)(userNameNotEmptyV(userName)),
    mapEither(() => userName)
  );

export class UnvalidatedUserName {
  public readonly key = 'Unvalidated';

  constructor(public readonly value: string) {}
}

export class InvalidUserName {
  public readonly key = 'Invalid';

  constructor(public readonly value: string, public readonly errors: string[]) {}
}

export class ValidUserName {
  public static create(userName: string): UserName {
    const usernameEither = pipe(
      validateUserName(userName),
      mapLeft(errors => new InvalidUserName(userName, errors)),
      mapEither(validUserName => new ValidUserName(validUserName))
    );

    return pipe(
      usernameEither,
      getOrElse(invalidUsername => invalidUsername as UserName)
    );
  }

  public readonly key = 'Valid';
  private constructor(public readonly value: string) {}
}

export type UserName = ValidUserName | InvalidUserName | UnvalidatedUserName;

export type UserNameOptions<TReturn> = ToMatchOptions<UserName, 'key', TReturn>;

export const matchUserName = <TReturn>(userName: UserName, options: UserNameOptions<TReturn>): TReturn => {
  switch (userName.key) {
    case 'Invalid':
      return options.Invalid(userName);
    case 'Valid':
      return options.Valid(userName);
    case 'Unvalidated':
      return options.Unvalidated(userName);
  }
};
